import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
    X, Loader2, Save, Trash2, Layout, Clock, Users,
    BookOpen, Check, AlertTriangle, ToggleLeft, ToggleRight, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { cn } from '../../lib/utils';
import api from '../../api';
import useCalendarStore from '../../store/useCalendarStore';

/**
 * TemplateDialog — view/edit/create templates
 * 
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   templateId: string | null (null = create mode)
 *   onSaved: () => void  (callback to refresh parent list)
 */
export default function TemplateDialog({ open, onClose, templateId, onSaved }) {
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Read working hours from global settings store
    const settings = useCalendarStore(s => s.settings);
    const gridStartMinutes = (() => {
        const wh = settings?.working_hours_start || '08:00';
        const [h, m] = wh.split(':').map(Number);
        return h * 60 + (m || 0);
    })();

    // Editable fields
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [isStarred, setIsStarred] = useState(false);

    const isCreate = !templateId;

    // Load template data
    useEffect(() => {
        if (!open) return;
        setConfirmDelete(false);
        if (isCreate) {
            setTemplate(null);
            setName('');
            setIsActive(true);
            return;
        }
        setLoading(true);
        api.get(`/api/templates/${templateId}`)
            .then(r => {
                setTemplate(r.data);
                setName(r.data.name || '');
                setIsActive(r.data.is_active !== false);
                setIsStarred(r.data.is_starred === true);
            })
            .catch(() => toast.error('Failed to load template'))
            .finally(() => setLoading(false));
    }, [open, templateId, isCreate]);

    const handleSave = async () => {
        setSaving(true);
        try {
            if (isCreate) {
                await api.post('/api/templates', {
                    name: name.trim() || 'Untitled Template',
                    scope: {},
                    events: [],
                });
                toast.success('Template created');
            } else {
                await api.put(`/api/templates/${templateId}`, {
                    name: name.trim(),
                    is_active: isActive,
                    is_starred: isStarred,
                });
                toast.success('Template updated');
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }
        setDeleting(true);
        try {
            await api.delete(`/api/templates/${templateId}`);
            toast.success('Template deleted');
            onSaved?.();
            onClose();
        } catch {
            toast.error('Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    if (!open) return null;

    const events = template?.events || [];

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg p-0 overflow-hidden" aria-describedby="template-dialog-description">
                <DialogHeader className="px-5 py-3.5 border-b border-zinc-100 dark:border-zinc-800">
                    <div className="flex items-center gap-2">
                        <Layout className="w-4 h-4 text-blue-500" />
                        <DialogTitle className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                            {isCreate ? 'New Template' : 'Edit Template'}
                        </DialogTitle>
                    </div>
                    <DialogDescription id="template-dialog-description" className="sr-only">
                        View and edit scheduling template patterns
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
                    </div>
                ) : (
                    <ScrollArea className="max-h-[60vh] overflow-y-auto">
                        <div className="p-5 space-y-5">
                            {/* Name */}
                            <div className="space-y-1.5">
                                <Label className="text-xs text-zinc-500">Template Name</Label>
                                 <div className="flex gap-2">
                                    <Input
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        placeholder="e.g. Monday Pattern, Week A Template"
                                        className="text-sm flex-1"
                                    />
                                    {!isCreate && (
                                        <button
                                            onClick={() => setIsStarred(!isStarred)}
                                            className={cn(
                                                "p-2 rounded-md border transition-all",
                                                isStarred 
                                                    ? "bg-amber-50 border-amber-200 text-amber-500 shadow-sm" 
                                                    : "bg-zinc-50 border-zinc-200 text-zinc-400 hover:text-amber-500 hover:border-amber-200"
                                            )}
                                            title={isStarred ? "Unstar Template" : "Star Template"}
                                        >
                                            <Star className={cn("w-4 h-4", isStarred && "fill-current")} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Active Toggle */}
                            {!isCreate && (
                                <div className="flex items-center justify-between py-2">
                                    <div>
                                        <Label className="text-xs text-zinc-500">Active</Label>
                                        <p className="text-[0.6rem] text-zinc-400">Active templates are available for auto-matching</p>
                                    </div>
                                    <button onClick={() => setIsActive(!isActive)}
                                        className="flex items-center gap-1.5 text-sm">
                                        {isActive
                                            ? <ToggleRight className="w-6 h-6 text-emerald-500" />
                                            : <ToggleLeft className="w-6 h-6 text-zinc-400" />}
                                    </button>
                                </div>
                            )}

                            {/* Stats Row */}
                            {!isCreate && template && (
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="data-card p-2.5 text-center">
                                        <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{events.length}</p>
                                        <p className="text-[0.6rem] text-zinc-400">Events</p>
                                    </div>
                                    <div className="data-card p-2.5 text-center">
                                        <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
                                            {template.quality_score ? `${Math.round(template.quality_score * 100)}%` : '—'}
                                        </p>
                                        <p className="text-[0.6rem] text-zinc-400">Quality</p>
                                    </div>
                                    <div className="data-card p-2.5 text-center">
                                        <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{template.match_count || 0}</p>
                                        <p className="text-[0.6rem] text-zinc-400">Day Matches</p>
                                    </div>
                                </div>
                            )}

                            {/* Events Table */}
                            {events.length > 0 && (
                                <div>
                                    <Label className="text-xs text-zinc-500 mb-2 block">Schedule Events</Label>
                                    <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-zinc-50 dark:bg-zinc-800/50 text-zinc-500">
                                                    <th className="text-left px-3 py-2 font-medium">Subject</th>
                                                    <th className="text-left px-3 py-2 font-medium">Faculty</th>
                                                    <th className="text-left px-3 py-2 font-medium">Batch</th>
                                                    <th className="text-right px-3 py-2 font-medium">Slot</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                                                {events.map((ev, i) => (
                                                    <tr key={i} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors">
                                                        <td className="px-3 py-1.5 text-zinc-700 dark:text-zinc-300 font-medium">
                                                            <div className="flex items-center gap-1.5">
                                                                <BookOpen className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                                                {ev.subject_name || ev.assignment_id?.slice(0, 8)}
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-1.5 text-zinc-500">
                                                            {ev.faculty_name || '—'}
                                                        </td>
                                                        <td className="px-3 py-1.5 text-zinc-500">
                                                            {ev.batch_name || '—'}
                                                        </td>
                                                        <td className="px-3 py-1.5 text-right text-zinc-400 tabular-nums">
                                                            {ev.start_slot != null
                                                                ? (() => {
                                                                    const totalMin = gridStartMinutes + ev.start_slot * 15;
                                                                    const h = Math.floor(totalMin / 60);
                                                                    const m = totalMin % 60;
                                                                    return `${h}:${String(m).padStart(2, '0')}`;
                                                                })()
                                                                : '—'}
                                                            {ev.duration_slots && ` (${ev.duration_slots * 15}m)`}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {isCreate && (
                                <div className="text-xs text-zinc-400 py-4 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg">
                                    <Layout className="w-5 h-5 mx-auto mb-1.5 text-zinc-300" />
                                    Empty template — run a Fixed Day pipeline and use "Save as Template" to populate.
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100 dark:border-zinc-800">
                    {!isCreate ? (
                        <div>
                            {confirmDelete ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-red-500 flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" /> Confirm?
                                    </span>
                                    <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}
                                        className="h-7 text-xs">
                                        {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Delete'}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}
                                        className="h-7 text-xs">Cancel</Button>
                                </div>
                            ) : (
                                <Button variant="ghost" size="sm" onClick={handleDelete}
                                    className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20">
                                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                                </Button>
                            )}
                        </div>
                    ) : <div />}

                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 text-xs">Cancel</Button>
                        <Button size="sm" onClick={handleSave} disabled={saving || !name.trim()}
                            className="h-8 text-xs bg-blue-600 hover:bg-blue-700">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                            {isCreate ? 'Create' : 'Save Changes'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
